import { Plus } from "lucide-react";

interface QA {
  q: string;
  a: React.ReactNode;
}

const FAQS: QA[] = [
  {
    q: "Is this legal in my country?",
    a: (
      <>
        Hupper is a peer-to-peer skill-based wagering protocol, not a casino — outcomes are
        determined by player skill, not chance. Legality of skill wagering varies by
        jurisdiction. We do not currently operate in restricted regions (US, UK, France).
        You are responsible for ensuring use complies with your local laws.
      </>
    ),
  },
  {
    q: "Who holds my funds?",
    a: (
      <>
        Nobody but you and the on-chain program. When you stake, SOL moves from your wallet
        into an escrow program on Solana that releases the pot to the winner automatically
        when the match ends. There's no withdrawal queue, and no step where Hupper takes
        custody of your balance.
      </>
    ),
  },
  {
    q: "What are the fees?",
    a: (
      <>
        A flat <span className="text-[var(--color-text-primary)] font-medium">10% protocol fee</span> is
        deducted from the winning pot — that's it. The Solana network fee (a fraction of a
        cent per action) is paid by the player initiating it. No deposit fees, no withdrawal
        fees, no spread, no hidden charges.
      </>
    ),
  },
  {
    q: "How fast are withdrawals?",
    a: (
      <>
        There are no withdrawals to request. Winnings settle directly to the winner's
        wallet the moment the match ends — usually within 3 seconds of the final point.
        You can move your SOL to any external wallet or exchange whenever you want.
      </>
    ),
  },
  {
    q: "How do I know the matches are fair?",
    a: (
      <>
        Game state is fully recorded — every paddle hit, shot, and round result is signed
        and submitted on-chain. Anyone can verify match integrity by inspecting the
        program's events on Solana Explorer. The escrow program is reviewed in-house,
        its upgrade authority held by a multisig, and{" "}
        <a
          href="https://github.com/leoarx/Hupper"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--color-text-primary)] underline underline-offset-2 decoration-[var(--color-line-emphasis)] hover:decoration-[var(--color-brand)] transition-colors duration-150"
        >
          open-source on GitHub
        </a>
        .
      </>
    ),
  },
];

function Item({ qa, index }: { qa: QA; index: number }) {
  return (
    <details
      className="group hairline-b last:border-b-0"
      // Keep first item open by default for visual demonstration
      {...(index === 0 ? { open: true } : {})}
    >
      <summary className="flex items-start justify-between gap-6 py-5 md:py-6 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <span className="text-[16px] md:text-[17px] font-display font-medium tracking-[-0.01em] text-[var(--color-text-primary)] leading-snug">
          {qa.q}
        </span>
        <span
          className="shrink-0 w-7 h-7 rounded-full hairline-t hairline-b hairline-l hairline-r flex items-center justify-center transition-all duration-200 group-open:rotate-45 group-open:bg-[var(--color-brand-soft)] group-open:border-[var(--color-brand-line)]"
        >
          <Plus
            className="w-3.5 h-3.5 text-[var(--color-text-secondary)] group-open:text-[var(--color-brand)]"
            strokeWidth={2}
          />
        </span>
      </summary>
      <div className="pb-6 md:pb-7 pr-12 text-[14px] leading-[1.65] text-[var(--color-text-secondary)] animate-fade-in-up">
        {qa.a}
      </div>
    </details>
  );
}

export default function FAQ() {
  return (
    <section
      id="faq"
      className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 w-full"
    >
      <div className="grid md:grid-cols-12 gap-10 md:gap-16">
        {/* Left column — title */}
        <div className="md:col-span-4 md:sticky md:top-24 md:self-start">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-tertiary)] font-medium mb-3">
            FAQ
          </div>
          <h2 className="text-[32px] md:text-[40px] font-display font-semibold tracking-[-0.025em] leading-[1.1] text-[var(--color-text-primary)]">
            Common questions, briefly.
          </h2>
          <p className="mt-4 text-[14px] text-[var(--color-text-secondary)] leading-relaxed">
            Anything else?{" "}
            <a
              href="mailto:support@hupper.com"
              className="text-[var(--color-text-primary)] underline underline-offset-2 decoration-[var(--color-line-emphasis)] hover:decoration-[var(--color-brand)] transition-colors duration-150"
            >
              Email support
            </a>
            {" "}— typical reply in under 12 hours.
          </p>
        </div>

        {/* Right column — accordion */}
        <div className="md:col-span-8">
          <div className="hairline-t">
            {FAQS.map((qa, i) => (
              <Item key={qa.q} qa={qa} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
